import { SdsProfileId } from "../../data/models/schemas";
import { SDS_ENABLE_ADDITIONAL_JURISDICTIONS } from "../utilities/FeatureFlags";
import { SDSFormat, type SDSDocumentFormat } from "../../types/sds-types";
import type { SDSFlaggedSelectOption } from "./components/SDSFlaggedSelect";

export type SdsComplianceOption = SDSFlaggedSelectOption & {
    profileId: SdsProfileId;
    format: SDSDocumentFormat;
    defaultLocaleId: string;
    labelKey: string;
};

export const DEFAULT_SDS_COMPLIANCE_PROFILE = SdsProfileId.US_OSHA_HAZCOM_EN_US;
export const DEFAULT_SDS_COMPLIANCE_FORMAT = SDSFormat.OSHA_HCS_2024;

export const SDS_COMPLIANCE_OPTIONS: SdsComplianceOption[] = [
    {
        id: "us-osha-hazcom-en-us",
        profileId: SdsProfileId.US_OSHA_HAZCOM_EN_US,
        format: SDSFormat.OSHA_HCS_2024,
        defaultLocaleId: "us-en",
        labelKey: "sds-compliance-choice-osha",
        flag: "🇺🇸",
        enabled: true,
    },
    {
        id: "canada-whmis-hpr-en-ca",
        profileId: SdsProfileId.CA_WHMIS_HPR_EN_CA,
        format: SDSFormat.CANADA_WHMIS_HPR_2022,
        defaultLocaleId: "ca-en",
        labelKey: "Canada WHMIS / English",
        flag: "🇨🇦",
        get enabled() {
            return SDS_ENABLE_ADDITIONAL_JURISDICTIONS();
        },
    },
    {
        id: "canada-whmis-hpr-fr-ca",
        profileId: SdsProfileId.CA_WHMIS_HPR_FR_CA,
        format: SDSFormat.CANADA_WHMIS_HPR_2022,
        defaultLocaleId: "ca-fr",
        labelKey: "Canada WHMIS / Français",
        flag: "🇨🇦",
        get enabled() {
            return SDS_ENABLE_ADDITIONAL_JURISDICTIONS();
        },
    },
    {
        id: "eu-reach-clp-de-de",
        profileId: SdsProfileId.EU_REACH_CLP_DE_DE,
        format: SDSFormat.EU_REACH_ANNEX_II_2020_878,
        defaultLocaleId: "de-de",
        labelKey: "sds-compliance-choice-eu-clp",
        flag: "🇩🇪",
        get enabled() {
            return SDS_ENABLE_ADDITIONAL_JURISDICTIONS();
        },
    },
    {
        id: "australia-model-whs-ghs7-en-au",
        profileId: SdsProfileId.AU_MODEL_WHS_GHS7_EN_AU,
        format: SDSFormat.AUSTRALIA_WHS_GHS_7,
        defaultLocaleId: "au-en",
        labelKey: "sds-compliance-choice-australia-whs",
        flag: "🇦🇺",
        get enabled() {
            return SDS_ENABLE_ADDITIONAL_JURISDICTIONS();
        },
    },
    {
        id: "japan-jis-2025-ja-jp",
        profileId: SdsProfileId.JP_JIS_7252_7253_JA_JP,
        format: SDSFormat.JAPAN_JIS_Z_7253_2025,
        defaultLocaleId: "jp-ja",
        labelKey: "sds-compliance-choice-japan-isha",
        flag: "🇯🇵",
        get enabled() {
            return SDS_ENABLE_ADDITIONAL_JURISDICTIONS();
        },
    },
];

const migrateLegacyProfile = (
    profileOrFormat: string | null | undefined,
    localeId?: string
): string | null | undefined => {
    switch (profileOrFormat) {
        case "US_OSHA_HAZCOM":
            return SdsProfileId.US_OSHA_HAZCOM_EN_US;
        case "CA_WHMIS_HPR":
            return localeId === "ca-fr"
                ? SdsProfileId.CA_WHMIS_HPR_FR_CA
                : SdsProfileId.CA_WHMIS_HPR_EN_CA;
        case "EU_REACH_CLP_DE":
            return SdsProfileId.EU_REACH_CLP_DE_DE;
        case "AU_MODEL_WHS_GHS7":
            return SdsProfileId.AU_MODEL_WHS_GHS7_EN_AU;
        case "JP_JIS_7252_7253":
            return SdsProfileId.JP_JIS_7252_7253_JA_JP;
        default:
            return profileOrFormat;
    }
};

export const getSdsComplianceOption = (
    profileOrFormat?: SdsProfileId | SDSDocumentFormat | string | null,
    localeId?: string
) => {
    const migrated = migrateLegacyProfile(profileOrFormat, localeId);
    return (
        SDS_COMPLIANCE_OPTIONS.find(
            (option) =>
                option.profileId === migrated || option.format === migrated
        ) ?? SDS_COMPLIANCE_OPTIONS[0]
    );
};
